import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  forwardRef,
  OnDestroy,
  QueryList,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { startWith, Subject, takeUntil } from 'rxjs';
import { SelectionCardComponent } from './selection-card.component';

@Component({
  selector: 'aura-selection-card-group',
  imports: [CommonModule],
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectionCardGroupComponent),
      multi: true,
    },
  ],
  host: {
    '[class.block]': 'true',
  },
})
export class SelectionCardGroupComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  @ContentChildren(SelectionCardComponent, { descendants: true })
  cards!: QueryList<SelectionCardComponent>;

  private value: unknown;
  private destroy$ = new Subject<void>();

  private onChange: (value: unknown) => void = () => {
    // Default implementation
  };
  private onTouched: () => void = () => {
    // Default implementation
  };

  ngAfterContentInit() {
    this.cards.changes.pipe(startWith(this.cards), takeUntil(this.destroy$)).subscribe(() => {
      this.setupCards();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.updateChildren();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.cards) {
      this.cards.forEach((card) => card.disabled.set(isDisabled));
    }
  }

  private setupCards() {
    this.cards.forEach((card) => {
      // Override card's internal selection logic to notify the group
      card.registerGroup(this);
    });
    this.updateChildren();
  }

  private updateChildren() {
    if (this.cards) {
      this.cards.forEach((card) => {
        card.selected.set(card.value() === this.value);
      });
    }
  }

  /**
   * Called by child cards when they are selected.
   */
  select(value: unknown) {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.updateChildren();
  }
}
